import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { analyticsApi, appointmentApi, adminWidgetsApi, formAssignmentApi, formDraftApi, reminderApi, formsApi, patientApi } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Users, Calendar, FileText, TrendingUp, TrendingDown,
  Clock, Activity, ArrowRight, RefreshCw, CalendarClock, FileWarning,
  Bell, Send, Plus, X, Edit3
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import IncidentsInboxWidget from '../../components/dashboard/IncidentsInboxWidget';
import IncidentTrendsWidget from '../../components/dashboard/IncidentTrendsWidget';
import ComplianceRiskAlert from '../../components/inservice/ComplianceRiskAlert';
import KudosWidget from '../../components/kudos/KudosWidget';

export default function PracticeAdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [widgets, setWidgets] = useState({ forms_due_this_month: [], forms_due_total: 0, drafts_total: 0, overdue_total: 0 });
  const [drafts, setDrafts] = useState([]);
  const [dueAssignments, setDueAssignments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Drill-down dialogs
  const [drilldownForm, setDrilldownForm] = useState(null);
  const [drilldownAssignments, setDrilldownAssignments] = useState([]);
  const [draftsOpen, setDraftsOpen] = useState(false);

  // Assign form dialog
  const [assignOpen, setAssignOpen] = useState(false);
  const [forms, setForms] = useState([]);
  const [patients, setPatients] = useState([]);
  const [newFormId, setNewFormId] = useState('');
  const [newPatientIds, setNewPatientIds] = useState([]);
  const [newDueDate, setNewDueDate] = useState('');
  const [newRecurrence, setNewRecurrence] = useState('none');
  const [newRecurrenceInterval, setNewRecurrenceInterval] = useState('');
  const [newRecurrenceEnd, setNewRecurrenceEnd] = useState('');
  const [assignSaving, setAssignSaving] = useState(false);

  // Reminder dialog
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderTarget, setReminderTarget] = useState(null);
  const [reminderTitle, setReminderTitle] = useState('');
  const [reminderMsg, setReminderMsg] = useState('');
  const [reminderEmail, setReminderEmail] = useState(true);
  const [reminderSending, setReminderSending] = useState(false);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const [analyticsRes, appointmentsRes, widgetsRes, draftsRes, dueRes] = await Promise.all([
        analyticsApi.orgOverview(),
        appointmentApi.list({ date: today }),
        adminWidgetsApi.get(),
        formDraftApi.list(),
        formAssignmentApi.list({ due_within_days: 30 }),
      ]);
      setAnalytics(analyticsRes.data);
      setAppointments(appointmentsRes.data.appointments || []);
      setWidgets(widgetsRes.data);
      setDrafts(draftsRes.data.drafts || []);
      setDueAssignments(dueRes.data.assignments || []);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const openAssignDialog = async () => {
    if (!forms.length) {
      try {
        const [fRes, pRes] = await Promise.all([formsApi.list(), patientApi.list({ limit: 500 })]);
        setForms(fRes.data.forms || []);
        setPatients(pRes.data || []);
      } catch { toast.error('Failed to load forms/patients'); return; }
    }
    setNewFormId(''); setNewPatientIds([]); setNewDueDate(''); setNewRecurrence('none'); setNewRecurrenceInterval(''); setNewRecurrenceEnd('');
    setAssignOpen(true);
  };

  const handleAssignSubmit = async () => {
    if (!newFormId || newPatientIds.length === 0 || !newDueDate) {
      toast.error('Form, patients and due date required'); return;
    }
    setAssignSaving(true);
    try {
      const payload = {
        form_id: newFormId,
        patient_ids: newPatientIds,
        due_date: newDueDate,
        recurrence: newRecurrence,
      };
      if (newRecurrence === 'custom') {
        const n = parseInt(newRecurrenceInterval, 10);
        if (!n || n < 1) { toast.error('Custom interval must be a positive number'); setAssignSaving(false); return; }
        payload.recurrence_interval_days = n;
      }
      if (newRecurrence !== 'none' && newRecurrenceEnd) payload.recurrence_end_date = newRecurrenceEnd;
      await formAssignmentApi.create(payload);
      toast.success(`Assigned to ${newPatientIds.length} patient(s)${newRecurrence !== 'none' ? ` (recurring ${newRecurrence})` : ''}`);
      setAssignOpen(false);
      loadDashboardData();
    } catch { toast.error('Failed to create assignments'); }
    finally { setAssignSaving(false); }
  };

  const openDrilldown = (form) => {
    const filtered = dueAssignments.filter(a => a.form_id === form.form_id && a.status !== 'completed');
    setDrilldownForm(form);
    setDrilldownAssignments(filtered);
  };

  const openReminder = (target) => {
    setReminderTarget(target);
    setReminderTitle(target?.title || 'Form reminder');
    setReminderMsg(target?.defaultMessage || '');
    setReminderEmail(true);
    setReminderOpen(true);
  };

  const sendReminder = async () => {
    setReminderSending(true);
    try {
      await reminderApi.send({
        user_ids: reminderTarget?.user_ids || [],
        patient_ids: reminderTarget?.patient_ids || [],
        title: reminderTitle,
        message: reminderMsg,
        link: reminderTarget?.link,
        send_email: reminderEmail,
      });
      toast.success('Reminder sent');
      setReminderOpen(false);
    } catch { toast.error('Failed to send reminder'); }
    finally { setReminderSending(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-[#0E7490] border-t-transparent rounded-full animate-spin" />
          <p className="text-[#64748B]">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const kpis = [
    { title: "Today's Patients", value: analytics?.kpis?.appointments_today || 0, icon: Users, trend: '+12%', trendUp: true, color: 'bg-[#0E7490]', onClick: () => navigate('/appointments'), testid: 'drilldown-admin-today' },
    { title: 'Total Patients', value: analytics?.kpis?.total_patients || 0, icon: Users, trend: '+5%', trendUp: true, color: 'bg-[#1A56DB]', onClick: () => navigate('/patients'), testid: 'drilldown-admin-patients' },
    { title: 'Forms Due This Month', value: widgets.forms_due_total, icon: CalendarClock, trend: `${widgets.overdue_total} overdue`, trendUp: widgets.overdue_total === 0, color: 'bg-[#7C3AED]', onClick: () => widgets.forms_due_this_month[0] && openDrilldown(widgets.forms_due_this_month[0]), testid: 'drilldown-admin-due-forms' },
    { title: 'Started but Unsubmitted', value: widgets.drafts_total, icon: Edit3, trend: drafts.length > 0 ? 'View drafts' : 'None', trendUp: drafts.length === 0, color: 'bg-[#CA8A04]', onClick: () => setDraftsOpen(true), testid: 'drilldown-admin-drafts' },
  ];

  return (
    <div className="space-y-8" data-testid="practice-admin-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>Operations Dashboard</h1>
          <p className="text-[#64748B] mt-1">Welcome back, {user?.name}. Here's your practice overview.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="careiq-btn-outline" onClick={openAssignDialog} data-testid="assign-form-btn"><Plus className="w-4 h-4 mr-2" />Assign Form</Button>
          <Button variant="outline" className="careiq-btn-outline" onClick={loadDashboardData} data-testid="refresh-dashboard"><RefreshCw className="w-4 h-4 mr-2" />Refresh</Button>
        </div>
      </div>

      {/* Open patient incidents — care coordinator's triage queue */}
      <IncidentsInboxWidget />
      {/* Patient safety analytics — falls/1000 patient-days, MTTR, top types */}
      <IncidentTrendsWidget />
      {/* In-service compliance risk — flags aides projected to miss YTD target */}
      <ComplianceRiskAlert />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpis.map((kpi, index) => (
          <Card key={index} className="careiq-metric-card animate-fade-in-up cursor-pointer hover:shadow-lg transition-shadow" style={{ animationDelay: `${index * 100}ms` }} onClick={kpi.onClick} data-testid={kpi.testid}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className={`w-12 h-12 ${kpi.color} rounded-xl flex items-center justify-center`}>
                  <kpi.icon className="w-6 h-6 text-white" />
                </div>
                <div className={`flex items-center gap-1 text-sm ${kpi.trendUp ? 'text-[#166534]' : 'text-[#991B1B]'}`}>
                  {kpi.trendUp ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                  {kpi.trend}
                </div>
              </div>
              <div className="mt-4">
                <div className="text-3xl font-bold text-[#0A2342]" style={{ fontFamily: 'Outfit, sans-serif' }}>{kpi.value}</div>
                <div className="text-sm text-[#64748B] mt-1">{kpi.title}</div>
              </div>
              <div className="mt-2 text-xs text-[#0E7490] flex items-center gap-1">View details <ArrowRight className="w-3 h-3" /></div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Forms Due This Month + Drafts */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card className="careiq-card" data-testid="forms-due-widget">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
              <CalendarClock className="w-5 h-5 text-[#7C3AED]" />Forms Due This Month
            </CardTitle>
            <span className="text-xs text-[#64748B]">{widgets.forms_due_this_month.length} form(s)</span>
          </CardHeader>
          <CardContent>
            {widgets.forms_due_this_month.length > 0 ? (
              <div className="space-y-2">
                {widgets.forms_due_this_month.map(f => (
                  <button key={f.form_id} onClick={() => openDrilldown(f)} className="w-full flex items-center justify-between p-3 rounded-lg hover:bg-[#F8FAFC] border border-[#E2E8F0] transition-colors text-left" data-testid={`due-form-${f.form_id}`}>
                    <div>
                      <div className="font-medium text-[#0A2342] text-sm">{f.form_name}</div>
                      <div className="text-xs text-[#64748B]">Nearest due: {f.nearest_due}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-1 bg-[#7C3AED]/10 text-[#7C3AED] rounded-full text-xs font-medium">{f.count} patient{f.count !== 1 ? 's' : ''}</span>
                      <ArrowRight className="w-4 h-4 text-[#94A3B8]" />
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-sm text-[#94A3B8]"><CalendarClock className="w-10 h-10 mx-auto mb-2" /><p>No forms due in the next 30 days</p><p className="text-xs mt-1">Click "Assign Form" to set one up</p></div>
            )}
          </CardContent>
        </Card>

        <Card className="careiq-card" data-testid="drafts-widget">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold text-[#0A2342] flex items-center gap-2">
              <Edit3 className="w-5 h-5 text-[#CA8A04]" />Started but Not Submitted
            </CardTitle>
            <span className="text-xs text-[#64748B]">{drafts.length} draft(s)</span>
          </CardHeader>
          <CardContent>
            {drafts.length > 0 ? (
              <div className="space-y-2 max-h-[320px] overflow-y-auto">
                {drafts.slice(0, 8).map(d => (
                  <div key={d.id} className="flex items-center justify-between p-3 rounded-lg border border-[#E2E8F0] hover:bg-[#F8FAFC]" data-testid={`draft-${d.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[#0A2342] text-sm truncate">{d.form_name}</div>
                      <div className="text-xs text-[#64748B]">
                        {d.patient_name || 'No patient'} • {d.submitted_by_name || 'User'} • {d.fields_filled} field(s)
                      </div>
                      <div className="text-[10px] text-[#94A3B8]">Updated {d.updated_at ? new Date(d.updated_at).toLocaleString() : ''}</div>
                    </div>
                    <Button size="sm" variant="outline" className="shrink-0" onClick={() => openReminder({
                      title: `Please complete: ${d.form_name}`,
                      defaultMessage: `Hi ${d.submitted_by_name || 'there'}, you started a "${d.form_name}" form but haven't submitted it yet. Please complete and submit when you can.`,
                      user_ids: d.submitted_by ? [d.submitted_by] : [],
                    })} data-testid={`draft-remind-${d.id}`}>
                      <Bell className="w-3.5 h-3.5 mr-1" />Remind
                    </Button>
                  </div>
                ))}
                {drafts.length > 8 && <button onClick={() => setDraftsOpen(true)} className="text-xs text-[#0E7490] hover:underline">View all {drafts.length} drafts</button>}
              </div>
            ) : (
              <div className="text-center py-8 text-sm text-[#94A3B8]"><FileWarning className="w-10 h-10 mx-auto mb-2" /><p>No drafts in progress</p></div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 careiq-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg font-semibold text-[#0A2342]">Weekly Appointments</CardTitle>
            <Link to="/appointments" className="text-sm text-[#0E7490] hover:underline flex items-center gap-1">View all <ArrowRight className="w-4 h-4" /></Link>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={analytics?.daily_appointments || []}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" stroke="#64748B" fontSize={12} tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { weekday: 'short' })} />
                <YAxis stroke="#64748B" fontSize={12} />
                <Tooltip contentStyle={{ backgroundColor: 'white', border: '1px solid #E2E8F0', borderRadius: '8px' }} />
                <Area type="monotone" dataKey="count" stroke="#0E7490" fill="#0E7490" fillOpacity={0.1} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="careiq-card">
          <CardHeader><CardTitle className="text-lg font-semibold text-[#0A2342]">Patient Risk Distribution</CardTitle></CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={[
                { name: 'High', value: analytics?.risk_distribution?.high || 0, fill: '#991B1B' },
                { name: 'Medium', value: analytics?.risk_distribution?.medium || 0, fill: '#92400E' },
                { name: 'Low', value: analytics?.risk_distribution?.low || 0, fill: '#166534' },
              ]} layout="vertical">
                <XAxis type="number" stroke="#64748B" fontSize={12} />
                <YAxis type="category" dataKey="name" stroke="#64748B" fontSize={12} width={60} />
                <Tooltip />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Today's Appointments */}
      <Card className="careiq-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg font-semibold text-[#0A2342]">Today's Schedule</CardTitle>
          <Link to="/appointments"><Button variant="outline" size="sm" className="careiq-btn-outline" data-testid="view-all-appointments">View All</Button></Link>
        </CardHeader>
        <CardContent>
          {appointments.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead><tr className="border-b border-[#E2E8F0]">
                  <th className="table-header text-left">Time</th><th className="table-header text-left">Patient</th><th className="table-header text-left">Type</th><th className="table-header text-left">Status</th><th className="table-header text-left">Action</th>
                </tr></thead>
                <tbody>
                  {appointments.slice(0, 5).map((appt) => (
                    <tr key={appt.id} className="table-row">
                      <td className="table-cell font-medium">{appt.scheduled_time}</td>
                      <td className="table-cell"><Link to={`/patients/${appt.patient_id}`} className="text-[#0E7490] hover:underline">{appt.patient_name}</Link></td>
                      <td className="table-cell capitalize">{appt.appointment_type.replace('_', ' ')}</td>
                      <td className="table-cell"><span className={`px-2 py-1 rounded-full text-xs font-medium ${appt.status === 'scheduled' ? 'bg-[#1A56DB]/10 text-[#1A56DB]' : appt.status === 'completed' ? 'bg-[#166534]/10 text-[#166534]' : appt.status === 'cancelled' ? 'bg-[#991B1B]/10 text-[#991B1B]' : 'bg-[#92400E]/10 text-[#92400E]'}`}>{appt.status}</span></td>
                      <td className="table-cell"><Button size="sm" variant="ghost" className="text-[#0E7490]">View</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-[#64748B]"><Calendar className="w-12 h-12 mx-auto mb-3 text-[#94A3B8]" /><p>No appointments scheduled for today</p></div>
          )}
        </CardContent>
      </Card>

      {/* Drilldown Dialog: patients with a form due */}
      <Dialog open={!!drilldownForm} onOpenChange={(o) => !o && setDrilldownForm(null)}>
        <DialogContent className="sm:max-w-2xl" data-testid="drilldown-dialog">
          <DialogHeader><DialogTitle>{drilldownForm?.form_name} — Patients Due</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[500px] overflow-y-auto mt-2">
            {drilldownAssignments.length > 0 ? drilldownAssignments.map(a => (
              <div key={a.id} className="flex items-center justify-between p-3 rounded-lg border border-[#E2E8F0] hover:bg-[#F8FAFC]" data-testid={`assignment-${a.id}`}>
                <div className="flex-1">
                  <button onClick={() => navigate(`/patients/${a.patient_id}`)} className="font-medium text-[#0E7490] hover:underline text-sm">{a.patient_name || a.patient_id}</button>
                  <div className="text-xs text-[#64748B] flex items-center gap-2 flex-wrap">
                    <span>Due: {a.due_date}</span>
                    <span>•</span>
                    <span>Assigned by {a.assigned_by_name}</span>
                    {a.recurrence && a.recurrence !== 'none' && (
                      <span className="px-1.5 py-0.5 bg-[#7C3AED]/10 text-[#7C3AED] rounded text-[10px] font-medium">↻ {a.recurrence}</span>
                    )}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => openReminder({
                  title: `Reminder: ${a.form_name} due ${a.due_date}`,
                  defaultMessage: `Hi ${a.patient_name}, please complete the "${a.form_name}" form by ${a.due_date}.`,
                  patient_ids: [a.patient_id],
                  link: `/patients/${a.patient_id}`,
                })} data-testid={`remind-patient-${a.patient_id}`}>
                  <Send className="w-3.5 h-3.5 mr-1" />Send Reminder
                </Button>
              </div>
            )) : <p className="text-center py-6 text-sm text-[#94A3B8]">No pending patients</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Drafts Modal */}
      <Dialog open={draftsOpen} onOpenChange={setDraftsOpen}>
        <DialogContent className="sm:max-w-2xl" data-testid="drafts-dialog">
          <DialogHeader><DialogTitle>All In-Progress Drafts</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[500px] overflow-y-auto mt-2">
            {drafts.length > 0 ? drafts.map(d => (
              <div key={d.id} className="flex items-center justify-between p-3 rounded-lg border border-[#E2E8F0]">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#0A2342] text-sm truncate">{d.form_name}</div>
                  <div className="text-xs text-[#64748B]">{d.patient_name || 'No patient'} • {d.submitted_by_name} • {d.fields_filled} field(s)</div>
                </div>
                <Button size="sm" variant="outline" onClick={() => openReminder({
                  title: `Please complete: ${d.form_name}`,
                  defaultMessage: `Hi ${d.submitted_by_name || 'there'}, please finish and submit "${d.form_name}".`,
                  user_ids: d.submitted_by ? [d.submitted_by] : [],
                })}><Bell className="w-3.5 h-3.5 mr-1" />Remind</Button>
              </div>
            )) : <p className="text-center py-6 text-sm text-[#94A3B8]">No drafts</p>}
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign Form Dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="assign-form-dialog">
          <DialogHeader><DialogTitle>Assign Form to Patients</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label>Form *</Label>
              <Select value={newFormId} onValueChange={setNewFormId}>
                <SelectTrigger data-testid="assign-form-select"><SelectValue placeholder="Choose form" /></SelectTrigger>
                <SelectContent>{forms.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Patients * ({newPatientIds.length} selected)</Label>
              <div className="max-h-48 overflow-y-auto border rounded-lg p-2 space-y-1">
                {patients.map(p => (
                  <label key={p.id} className="flex items-center gap-2 text-xs p-1 rounded hover:bg-[#F8FAFC] cursor-pointer">
                    <input type="checkbox" checked={newPatientIds.includes(p.id)} onChange={(e) => {
                      if (e.target.checked) setNewPatientIds([...newPatientIds, p.id]);
                      else setNewPatientIds(newPatientIds.filter(id => id !== p.id));
                    }} />
                    <span>{p.first_name} {p.last_name}</span>
                    <span className="text-[#94A3B8] ml-auto">{p.mrn}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label>Due Date *</Label>
              <Input type="date" value={newDueDate} onChange={e => setNewDueDate(e.target.value)} data-testid="assign-due-date" />
            </div>
            <div>
              <Label>Recurrence</Label>
              <Select value={newRecurrence} onValueChange={setNewRecurrence}>
                <SelectTrigger data-testid="assign-recurrence"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">One-time (no recurrence)</SelectItem>
                  <SelectItem value="weekly">Weekly (every 7 days)</SelectItem>
                  <SelectItem value="monthly">Monthly (every 30 days)</SelectItem>
                  <SelectItem value="quarterly">Quarterly (every 90 days)</SelectItem>
                  <SelectItem value="custom">Custom interval</SelectItem>
                </SelectContent>
              </Select>
              {newRecurrence === 'custom' && (
                <Input type="number" min="1" value={newRecurrenceInterval} onChange={e => setNewRecurrenceInterval(e.target.value)} placeholder="Interval in days (e.g. 14)" className="mt-2" data-testid="assign-recurrence-interval" />
              )}
              {newRecurrence !== 'none' && (
                <div className="mt-2">
                  <Label className="text-xs">End on (optional)</Label>
                  <Input type="date" value={newRecurrenceEnd} onChange={e => setNewRecurrenceEnd(e.target.value)} data-testid="assign-recurrence-end" />
                  <p className="text-[10px] text-[#94A3B8] mt-1">Next cycle auto-creates once a patient submits the current assignment.</p>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAssignOpen(false)}>Cancel</Button>
              <Button className="careiq-btn-primary" onClick={handleAssignSubmit} disabled={assignSaving} data-testid="assign-submit-btn">{assignSaving ? 'Assigning...' : 'Assign'}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reminder Dialog */}
      <Dialog open={reminderOpen} onOpenChange={setReminderOpen}>
        <DialogContent className="sm:max-w-md" data-testid="reminder-dialog">
          <DialogHeader><DialogTitle>Send Reminder</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div><Label>Title</Label><Input value={reminderTitle} onChange={e => setReminderTitle(e.target.value)} data-testid="reminder-title-input" /></div>
            <div><Label>Message</Label><textarea value={reminderMsg} onChange={e => setReminderMsg(e.target.value)} className="w-full min-h-[100px] border rounded-lg p-2 text-sm" data-testid="reminder-message-input" /></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={reminderEmail} onChange={e => setReminderEmail(e.target.checked)} />Also queue email</label>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setReminderOpen(false)}>Cancel</Button>
              <Button className="careiq-btn-primary" onClick={sendReminder} disabled={reminderSending} data-testid="send-reminder-btn">{reminderSending ? 'Sending...' : <><Send className="w-4 h-4 mr-1" />Send</>}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
